/**
 * Tombol aksi mana yang boleh MUNCUL untuk sebuah tugas — MURNI.
 *
 * Modul ini MENIRU state machine server (`field-task-lifecycle.ts`, ADR-0035 C3) dengan sadar.
 * Duplikasi itu disengaja dan punya batas yang jelas:
 *
 * - Server tetap satu-satunya yang MENEGAKKAN. Transisi tidak sah ditolak `TASK-002`, aktor
 *   bukan-assignee ditolak `TASK-003`, apa pun yang ditampilkan klien.
 * - Yang dikerjakan di sini hanya mencegah tombol yang pasti gagal muncul. Menekan "Mulai"
 *   pada tugas yang belum di-accept lalu ditolak server terasa seperti aplikasi rusak,
 *   padahal aturannya benar.
 *
 * Karena ia tiruan, ia bisa menyimpang. Yang menahannya adalah test: kedelapan status diuji
 * satu per satu terhadap tabel transisi yang sama dengan milik server. Bila server berubah dan
 * modul ini tidak, test itu yang gagal lebih dulu.
 *
 * Status adalah SATU-SATUNYA sumber kebenaran, dan ia datang dari server. Modul ini tidak
 * pernah menyimpulkan status dari event, waktu, atau tindakan terakhir pengguna.
 */

import { hasAnyPermission } from './navigation';

export const FIELD_TASK_STATUSES = [
  'draft',
  'assigned',
  'accepted',
  'started',
  'arrived',
  'completed',
  'failed',
  'cancelled',
] as const;

export type FieldTaskStatus = (typeof FIELD_TASK_STATUSES)[number];

/**
 * Aksi yang ditangani slice ini.
 *
 * `complete` dan `fail` SENGAJA tidak ada: keduanya terikat bukti pengiriman dan dikerjakan di
 * Issue #465/#466. Menampilkannya sekarang berarti menawarkan tombol yang tidak punya layar
 * lanjutan — pengguna sampai di jalan buntu, bukan sekadar ditolak.
 */
export type TaskAction = 'assign' | 'accept' | 'start' | 'arrive' | 'cancel';

export const TERMINAL_STATUSES: readonly FieldTaskStatus[] = ['completed', 'failed', 'cancelled'];

export function isTerminal(status: FieldTaskStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** Izin yang dituntut tiap aksi — kunci sama persis dengan `@RequirePermission` di controller. */
const PERMISSION_FOR: Readonly<Record<TaskAction, string>> = {
  assign: 'workforce.task.assign',
  accept: 'workforce.task.accept',
  start: 'workforce.task.start',
  arrive: 'workforce.task.arrive',
  cancel: 'workforce.task.cancel',
};

/**
 * Aksi yang menuntut aktor = assignee (TASK-003).
 *
 * `assign` dan `cancel` TIDAK termasuk — keduanya milik dispatcher/manajer, yang justru bukan
 * pelaksana tugas. Menyamakan seluruh aksi sebagai "hanya assignee" akan menyembunyikan tombol
 * batal dari satu-satunya orang yang berwenang menekannya.
 */
export function requiresAssignee(action: TaskAction): boolean {
  return action === 'accept' || action === 'start' || action === 'arrive';
}

/**
 * Legalitas transisi menurut status — cerminan `resolveTransition` milik server.
 *
 * `assign` legal dari `draft`, `assigned`, dan `accepted`. Dua yang terakhir adalah SELF-LOOP:
 * reassign tidak mereset status ke `assigned`, assignee baru mewarisi status apa adanya. Sesudah
 * `started`, reassign ditolak `TASK-008`.
 */
export function isTransitionLegal(status: FieldTaskStatus, action: TaskAction): boolean {
  switch (action) {
    case 'assign':
      return status === 'draft' || status === 'assigned' || status === 'accepted';
    case 'accept':
      return status === 'assigned';
    case 'start':
      return status === 'accepted';
    case 'arrive':
      return status === 'started';
    case 'cancel':
      return !isTerminal(status);
  }
}

export interface TaskActionContext {
  readonly status: FieldTaskStatus;
  readonly isAssignee: boolean;
  readonly permissions: readonly string[];
}

/**
 * Tombol yang layak ditampilkan, dalam urutan alur kerja lapangan.
 *
 * Urutannya bukan selera: `accept → start → arrive` adalah urutan yang dilakukan orang, dan
 * `cancel` selalu terakhir supaya tidak pernah berada di bawah ibu jari saat seseorang
 * bergegas menekan tombol utama.
 */
const ACTION_ORDER: readonly TaskAction[] = ['accept', 'start', 'arrive', 'assign', 'cancel'];

export function availableActions(ctx: TaskActionContext): readonly TaskAction[] {
  return ACTION_ORDER.filter((action) => {
    if (!isTransitionLegal(ctx.status, action)) return false;
    if (requiresAssignee(action) && !ctx.isAssignee) return false;
    return hasAnyPermission(ctx.permissions, [PERMISSION_FOR[action]]);
  });
}

const ACTION_LABEL: Readonly<Record<TaskAction, string>> = {
  assign: 'Tugaskan',
  accept: 'Terima tugas',
  start: 'Mulai',
  arrive: 'Sampai di lokasi',
  cancel: 'Batalkan',
};

export function formatAction(action: TaskAction): string {
  return ACTION_LABEL[action];
}

/** Aksi yang WAJIB mengirim lokasi + akurasi (API-CONTRACT §3). */
export function requiresLocation(action: TaskAction): boolean {
  return action === 'start' || action === 'arrive';
}

const STATUS_LABEL: Readonly<Record<FieldTaskStatus, string>> = {
  draft: 'Draf',
  assigned: 'Ditugaskan',
  accepted: 'Diterima',
  started: 'Berjalan',
  arrived: 'Sampai lokasi',
  completed: 'Selesai',
  failed: 'Gagal',
  cancelled: 'Dibatalkan',
};

export function formatTaskStatus(status: string): string {
  return STATUS_LABEL[status as FieldTaskStatus] ?? status;
}

/**
 * Benar bila tugas sudah BUKAN milik pemakai lagi — ia pernah menjadi assignee, kini bukan.
 *
 * Ditampilkan sebagai pernyataan tegas, bukan sekadar tombol yang hilang. Kurir yang tugasnya
 * dialihkan di tengah jalan harus tahu bahwa ia berhenti mengerjakannya; tombol yang lenyap
 * tanpa penjelasan akan dibaca sebagai aplikasi bermasalah, dan ia tetap berangkat.
 */
export function wasReassignedAway(
  assigneeUserId: string | null,
  viewerUserId: string,
  viewerWasAssignee: boolean,
): boolean {
  return viewerWasAssignee && assigneeUserId !== viewerUserId;
}

/**
 * Peristiwa yang HANYA dapat dilakukan PELAKSANA tugas itu sendiri.
 *
 * `assigned`, `reassigned`, `created`, `cancelled`, dan `note_added` sengaja TIDAK ada di sini:
 * pelakunya adalah penyelia, bukan orang yang mengerjakan. Justru itulah cacat yang daftar ini
 * memperbaikinya.
 */
const PERISTIWA_PELAKSANA: readonly string[] = [
  'accepted',
  'started',
  'arrived',
  'completed',
  'failed',
];

/**
 * Benarkah pemakai ini PERNAH menjadi pelaksana tugas tersebut — dibaca dari riwayatnya.
 *
 * ADA KARENA cacat yang terlihat di produksi 24 September 2026. Layar detail tugas menghitungnya
 * sebagai "pemakai muncul sebagai aktor di peristiwa MANA PUN":
 *
 *     const viewerAppearsInEvents = events.some((e) => e.actorUserId === session.me.userId);
 *
 * Menugaskan tugas MENERBITKAN peristiwa `assigned` dengan penyelia sebagai aktornya. Maka admin
 * yang baru saja menugaskan sebuah pengiriman, lalu membuka tugasnya untuk memeriksa, membaca:
 *
 *     "Tugas ini bukan lagi milik Anda. Penyelia mengalihkannya ke pelaksana lain.
 *      Jangan melanjutkan pengerjaan; hubungi penyelia Anda bila sudah terlanjur berangkat."
 *
 * Ia adalah penyelianya. Tidak ada yang dialihkan. Dan menugaskan adalah pekerjaan admin yang
 * paling sering — jadi pesan ini muncul pada hari pertama setiap pelanggan, di layar yang justru
 * dibuka untuk memastikan penugasannya berhasil.
 *
 * MENJADI AKTOR ≠ MENJADI PELAKSANA. Riwayat tugas tidak menyimpan siapa assignee SEBELUMNYA,
 * jadi pertanyaan itu tidak dapat dijawab langsung dari sini. Yang dapat dijawab, dan yang
 * sebenarnya dimaksud: apakah orang ini pernah MENGERJAKANNYA — menerima, berangkat, sampai,
 * menyelesaikan, atau menggagalkannya. Kelimanya hanya mungkin dilakukan pelaksananya sendiri.
 *
 * KONSEKUENSI YANG DISENGAJA: kurir yang ditugaskan lalu dialihkan SEBELUM ia menyentuh tugasnya
 * tidak lagi melihat spanduk itu. Itu memang batas yang benar untuk ditarik — peringatannya ada
 * untuk orang yang "dialihkan di tengah jalan" (lihat `wasReassignedAway`), dan orang yang belum
 * pernah menekan Terima tidak sedang di jalan. Menukarnya dengan peringatan palsu yang pasti
 * muncul setiap hari bukan pertukaran yang setara.
 */
export function pernahMenjadiPelaksana(
  events: readonly { readonly eventType: string; readonly actorUserId: string }[],
  viewerUserId: string,
): boolean {
  return events.some(
    (e) => e.actorUserId === viewerUserId && PERISTIWA_PELAKSANA.includes(e.eventType),
  );
}

/**
 * Benar bila tugas ini boleh DIHAPUS — cerminan `dapatDihapus` di server.
 *
 * Hapus ≠ Batalkan. `cancel` untuk pekerjaan nyata yang tidak jadi dilakukan (riwayatnya tetap
 * ada, dan memang harus). `delete` untuk tugas yang seharusnya tidak pernah ada: salah ketik,
 * salah pelanggan, tertekan dua kali.
 *
 * Batasnya: hanya selama tugas belum DITERIMA kurir. Sesudah itu ia berhenti menjadi
 * salah-masukan dan menjadi catatan kerja seseorang.
 *
 * Ini MEMANDU tampilan; server tetap MENEGAKKAN (`TASK-442`). Layar bisa basi — tugas yang
 * tampak `draft` di ponsel manajer mungkin baru saja diterima kurir.
 */
export function canDeleteTask(status: FieldTaskStatus, permissions: readonly string[]): boolean {
  if (!permissions.includes('workforce.task.delete')) return false;
  return status === 'draft' || status === 'assigned';
}
